import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import "./EmptyState.css";

export interface EmptyStateProps {
  /**
   * The main title of the empty state
   */
  title: string;

  /**
   * Optional description text
   */
  description?: string;

  /**
   * Optional icon to display
   * If not provided, a default search icon will be used
   */
  icon?: React.ReactNode;

  /**
   * Optional action element (usually a button)
   */
  action?: React.ReactNode;

  /**
   * Size variant for the empty state
   */
  size?: "sm" | "md" | "lg";

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Optional image to display instead of an icon
   */
  image?: string;

  /**
   * Test ID for testing
   */
  testId?: string;
}

/**
 * EmptyState component displays a message when there is no content to show
 * Optionally includes an action to help the user proceed
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  size = "md",
  className = "",
  image,
  testId = "empty-state",
}) => {
  const renderIcon = () => {
    if (image) {
      return (
        <img
          src={image}
          alt={title}
          className="empty-state-image"
          data-testid={`${testId}-image`}
        />
      );
    }

    return (
      <div className={`empty-state-icon ${size}`} data-testid={`${testId}-icon`}>
        {icon || <FontAwesomeIcon icon={faSearch} />}
      </div>
    );
  };

  return (
    <div className={`empty-state ${size} ${className}`} data-testid={testId}>
      {renderIcon()}

      <h3 className="empty-state-title" data-testid={`${testId}-title`}>
        {title}
      </h3>

      {description && (
        <p className="empty-state-description" data-testid={`${testId}-description`}>
          {description}
        </p>
      )}

      {action && (
        <div className="empty-state-action" data-testid={`${testId}-action`}>
          {action}
        </div>
      )}
    </div>
  );
};

export default EmptyState;
